// ---------------------------------------------------------------------------
// DnD Email Editor – Columns math & reconciliation (single source of truth)
//
// Both the canvas preview (DndCanvas) and the HTML compiler (dnd-compiler)
// import from here so column widths and counts can never drift between what
// the user sees and what gets exported.
// ---------------------------------------------------------------------------

import type { Column, ColumnsLayer, ColumnsLayout } from './dnd-model';
import { defaultColumn } from './dnd-defaults';

/** Number of column cells each layout preset expects. */
export const LAYOUT_COLUMN_COUNTS: Record<ColumnsLayout, number> = {
  '1': 1,
  '2': 2,
  '3': 3,
  '4': 4,
  '1:2': 2,
  '2:1': 2,
  '1:3': 2,
  '3:1': 2,
};

/** Width percentages for each layout preset (must sum to ~100). */
export const COLUMN_WIDTH_RATIOS: Record<ColumnsLayout, number[]> = {
  '1': [100],
  '2': [50, 50],
  '3': [33.33, 33.34, 33.33],
  '4': [25, 25, 25, 25],
  '1:2': [33.33, 66.67],
  '2:1': [66.67, 33.33],
  '1:3': [25, 75],
  '3:1': [75, 25],
};

/**
 * Resolve the width percentages for a columns layer. Prefers an explicit
 * `columnWidths` override when it is present and valid (right length, sums to
 * ~100); otherwise falls back to the layout preset. This is the seam that lets
 * custom widths be added later without touching call sites.
 */
export function getColumnWidths(layer: ColumnsLayer): number[] {
  const preset = COLUMN_WIDTH_RATIOS[layer.layout] ?? [100];
  const custom = layer.columnWidths;
  if (
    custom &&
    custom.length === layer.columns.length &&
    custom.every((n) => Number.isFinite(n) && n > 0) &&
    Math.abs(custom.reduce((a, b) => a + b, 0) - 100) < 1
  ) {
    return custom;
  }
  return preset;
}

/**
 * Resolve each column's rendered pixel width given the content max width.
 * Mirrors the compiler's existing rounding exactly so preview and export agree.
 */
export function getColumnPixelWidths(layer: ColumnsLayer, maxWidth: number): number[] {
  return getColumnWidths(layer).map((pct) => Math.round((maxWidth * pct) / 100));
}

/**
 * Grow or shrink a columns array to match a target count.
 * - equal count  → returned unchanged (covers all ratio↔ratio and ratio↔2 switches)
 * - grow         → keep all existing columns, append fresh empty ones
 * - shrink       → keep the first `target` columns and MERGE every removed
 *                  column's elements into the last retained column (no data loss)
 */
export function reconcileColumns(columns: Column[], targetCount: number): Column[] {
  const count = columns.length;
  if (targetCount === count || targetCount < 1) return columns;

  if (targetCount > count) {
    const added = Array.from({ length: targetCount - count }, () => defaultColumn());
    return [...columns, ...added];
  }

  // Shrink: keep first `target`, fold trailing columns' elements into the last kept.
  const kept = columns.slice(0, targetCount).map((c) => ({ ...c, elements: [...c.elements] }));
  const removed = columns.slice(targetCount);
  const lastKept = kept[kept.length - 1];
  for (const col of removed) {
    lastKept.elements.push(...col.elements);
  }
  return kept;
}

/**
 * Build the patch for changing a columns layer's layout: updates the layout
 * string AND reconciles the columns array (and trims any custom widths) so the
 * two never disagree. Hand the result straight to `updateLayer`.
 */
export function changeColumnsLayout(
  layer: ColumnsLayer,
  newLayout: ColumnsLayout,
): Partial<ColumnsLayer> {
  const target = LAYOUT_COLUMN_COUNTS[newLayout] ?? layer.columns.length;
  const columns = reconcileColumns(layer.columns, target);
  const patch: Partial<ColumnsLayer> = { layout: newLayout, columns };
  // A preset change always resets to preset widths.
  if (layer.columnWidths) patch.columnWidths = undefined;
  return patch;
}
